import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { ArrowRight, ExternalLink, Globe, Smartphone, CheckCircle2 } from 'lucide-react';

const featuredApps = [
  {
    id: 'stir',
    name: 'Stir',
    tagline: 'Your personal cooking assistant',
    description:
      'Import recipes from TikTok, Instagram, YouTube or any website with a single tap. Plan meals, track 7 nutrients per serving, and let Stir auto-generate your weekly shopping list.',
    accent: 'emerald',
    badgeColor: 'bg-emerald-600 text-white',
    cardGradient: 'from-emerald-500/10 via-card/60 to-amber-500/5',
    iconImg: 'https://customer-assets.emergentagent.com/job_c2c4f44d-f7c5-4fd0-bbd5-451d182ee18d/artifacts/b98ntxjh_App%20icon%20to%20use.png',
    image:
      'https://customer-assets.emergentagent.com/job_a2c0c71c-d861-4f90-8265-579e8463e6c3/artifacts/6pbf5ac1_homepage%20-%20explore%20recipies%20that%20are%20imported%20or%20created%20by%20other%20users.png',
    features: ['One-tap social import', 'AI chef assistant', 'Smart meal planner', 'Auto shopping lists', 'Nutrition tracking'],
    stats: [
      { value: '1000+', label: 'Recipes imported' },
      { value: '7', label: 'Nutrients tracked' },
      { value: '30s', label: 'Avg import time' },
    ],
    links: {
      appStore: 'https://apps.apple.com/gb/app/stir-your-recipe-assistant/id6760202603',
      googlePlay: 'https://play.google.com/store/apps/details?id=io.tryweb.cookflow',
      website: 'https://stir-it.app',
    },
  },
  {
    id: 'kisses',
    name: 'Kisses',
    tagline: 'The beautiful pet ID app',
    description:
      'A soft, pastel pet-ID app. Health, schedule, vet contacts and house rules — all in one beautiful profile. Share with a tap via link, QR code or NFC. Lost-pet mode finds them if they ever wander.',
    accent: 'rose',
    badgeColor: 'bg-rose-500 text-white',
    cardGradient: 'from-rose-300/10 via-card/60 to-emerald-200/5',
    iconImg: 'https://kisses-id.app/assets/assets/images/icon.d174aa754565547125840e90e39f4bdf.png',
    features: ['Complete pet profile', 'Tap-to-share (NFC)', 'Lost-pet mode', 'Sitter & walker access', 'Curated pet shop'],
    stats: [
      { value: '2k+', label: 'Pets registered' },
      { value: '8', label: 'Countries' },
      { value: '24/7', label: 'Lost-mode' },
    ],
    links: {
      appStore: 'https://kisses-id.app/',
      googlePlay: 'https://kisses-id.app/',
      website: 'https://kisses-id.app/',
    },
  },
];

const ProjectsPage = () => {
  useEffect(() => {
    document.title = 'Projects | TryWeb';
  }, []);

  return (
    <PageLayout>
      {/* Hero */}
      <section className="relative py-24 hero-glow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-[0.2em] text-primary mb-3">Portfolio</p>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              <span className="text-gradient">Apps & sites we've shipped</span>
            </h1>
            <p className="text-xl text-muted-foreground">
              From our own studio products live on the App Store, to AI-generated websites for UK businesses — here's a slice of the work.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Apps */}
      <section className="pt-8 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10 pt-8 border-t border-white/5">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold">Featured apps</h2>
              <p className="text-muted-foreground mt-2">Live on iOS & Android</p>
            </div>
          </div>

          <div className="space-y-12">
            {featuredApps.map((app, idx) => (
              <div
                key={app.id}
                className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${idx % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''}`}
                data-testid={`featured-app-${app.id}`}
              >
                {/* Visual */}
                <div className={`relative rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br ${app.cardGradient} aspect-[4/3] flex items-center justify-center p-8`}>
                  {app.image ? (
                    <img
                      src={app.image}
                      alt={`${app.name} app screenshot`}
                      className="max-h-full w-auto rounded-2xl shadow-2xl"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-6">
                      <div className="w-32 h-32 rounded-[2rem] overflow-hidden bg-white/95 shadow-2xl">
                        <img src={app.iconImg} alt={`${app.name} logo`} className="w-full h-full object-cover" />
                      </div>
                      <p className="text-2xl font-semibold text-foreground/90">{app.name}</p>
                      <p className="text-sm text-muted-foreground">{app.tagline}</p>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div>
                  <div className="flex items-center gap-4 mb-5">
                    {app.iconImg ? (
                      <div className="w-14 h-14 rounded-2xl overflow-hidden bg-white/90">
                        <img src={app.iconImg} alt={`${app.name} logo`} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className={`w-14 h-14 rounded-2xl ${app.iconStyle} flex items-center justify-center text-2xl font-bold text-white shadow-lg`}>
                        {app.iconChar}
                      </div>
                    )}
                    <div>
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${app.badgeColor}`}>
                        <Smartphone className="w-3 h-3" /> Mobile App
                      </span>
                      <h3 className="text-3xl font-bold mt-1">{app.name}</h3>
                    </div>
                  </div>

                  <p className="text-muted-foreground mb-6 leading-relaxed">{app.description}</p>

                  <ul className="grid grid-cols-2 gap-2 mb-6">
                    {app.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className={`w-4 h-4 ${app.accent === 'emerald' ? 'text-emerald-400' : 'text-rose-300'} shrink-0`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="grid grid-cols-3 gap-3 mb-6 pb-6 border-b border-white/5">
                    {app.stats.map((s) => (
                      <div key={s.label}>
                        <p className={`text-2xl font-bold ${app.accent === 'emerald' ? 'text-emerald-400' : 'text-rose-300'}`}>{s.value}</p>
                        <p className="text-xs text-muted-foreground">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <a href={app.links.appStore} target="_blank" rel="noopener noreferrer" data-testid={`${app.id}-appstore`}>
                      <Button variant="outline" className="gap-2">
                        App Store
                      </Button>
                    </a>
                    <a href={app.links.googlePlay} target="_blank" rel="noopener noreferrer" data-testid={`${app.id}-googleplay`}>
                      <Button variant="outline" className="gap-2">
                        Google Play
                      </Button>
                    </a>
                    <a href={app.links.website} target="_blank" rel="noopener noreferrer" data-testid={`${app.id}-website`}>
                      <Button variant="ghost" className="gap-2">
                        Visit site <ExternalLink className="w-4 h-4" />
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Websites we build */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold">Websites for local businesses</h2>
            <p className="text-muted-foreground mt-2">
              We use our own AI tooling to generate beautiful, fast-loading websites for tradespeople, salons, restaurants and more.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { cat: 'Restaurants & cafés', icon: Globe },
              { cat: 'Tradespeople (plumbers, electricians, roofers)', icon: Globe },
              { cat: 'Beauty & wellness', icon: Globe },
              { cat: 'Auto services', icon: Globe },
              { cat: 'Professional services', icon: Globe },
              { cat: 'Home services & landscaping', icon: Globe },
            ].map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.cat} className="bg-card/50 border border-white/5 rounded-xl p-6 card-hover">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <p className="font-medium">{c.cat}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-card/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Want us to build yours?</h2>
          <p className="text-muted-foreground mb-8">
            Whether it's a website for your business or a brand-new product idea — we'd love to hear about it.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="glow-primary gap-2" data-testid="projects-cta">
                Start a project <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default ProjectsPage;
